import React, {useState} from 'react';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {useSession} from '../../../../firebase/UserProvider';
const _awpBase = process.env.REACT_APP_AWP_API_URL || 'https://awp.minsky.ai';
function _authH() { const t = localStorage.getItem('access_token'); return t ? {Authorization: `Bearer ${t}`, 'Content-Type': 'application/json'} : {'Content-Type': 'application/json'}; }

/**
 * EditableSection
 * Props:
 * - title: string (display title)
 * - items: array (current items to display)
 * - fieldKey: string (field name in Firestore document)
 * - feedId: string (document id)
 * - onSaved?: function(newItems) called after successful save
 */

const EditableSection = ({title, items = [], fieldKey, feedId, onSaved}) => {
    const {user} = useSession() || {};
    const canEdit = Boolean(user);

    const [open, setOpen] = useState(false);
    const [value, setValue] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const handleOpen = () => {
        setValue((items || []).map(i => (typeof i === 'string' ? i : JSON.stringify(i))).join('\n'));
        setError(null);
        setOpen(true);
    };

    const handleClose = () => {
        if (saving) return;
        setOpen(false);
        setError(null);
    };

    const parseList = (str) => {
        if (!str) return [];
        return str.split(/\r?\n/).map(s => s.trim()).filter(Boolean);
    };

    const handleSave = async () => {
        console.log(fieldKey, feedId)
        if (!fieldKey || !feedId) {
            setError('Missing target');
            return;
        }
        const next = parseList(value);
        setSaving(true);
        setError(null);
        try {
            await fetch(`${_awpBase}/api/feeds/${encodeURIComponent(feedId)}`, {
                method: 'PATCH', headers: _authH(),
                body: JSON.stringify({[fieldKey]: next, lastUpdated: new Date().toISOString()}),
            });
            setOpen(false);
            if (typeof onSaved === 'function') onSaved(next);
        } catch (err) {
            console.error('Failed to save section', err);
            setError(err.message || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" fontWeight={600}>{title}</Typography>
                {canEdit && (
                    <Button size="small" variant="text" onClick={handleOpen}>Edit</Button>
                )}
            </Stack>

            <Stack direction="row" gap={1} flexWrap="wrap">
                {(items || []).map((it, idx) => (
                    <Typography
                        key={idx}
                        variant={'body2'}
                    >{typeof it === 'string' ? it : it.name || JSON.stringify(it)}</Typography>
                ))}
            </Stack>

            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
                <DialogTitle>Edit {title}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{mt: 1}}>
                        <TextField
                            label={`${title} (one per line)`}
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            fullWidth
                            multiline
                            minRows={6}
                        />
                        {error && <Typography color="error">{error}</Typography>}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={saving}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained" disabled={saving}>
                        {saving ? <CircularProgress size={18}/> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};

export default EditableSection;

